/**
 * BP-026 · Platform misc pages — Revenue, AI Admin, Health, Support,
 * Announcements, Roles. Each is deliberately lean (list + primary action)
 * so Session 3 ships a full console; deep detail views land in later
 * polish.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  DollarSign, Cpu, Server, LifeBuoy, Megaphone, UserCog, Plus,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";

// ─── Revenue ────────────────────────────────────────────

export function PlatformRevenue() {
  const [d, setD] = useState(null);
  useEffect(() => {
    apiGet("/api/v3/platform/revenue?window_days=30").then(setD);
  }, []);
  if (!d) return <div className="p-8 text-slate-600">Loading…</div>;
  return (
    <div data-testid="platform-revenue" className="p-8 space-y-4">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <DollarSign className="w-4 h-4" /> Revenue · 30d
        </div>
        <h1 className="text-2xl font-bold">Platform revenue</h1>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["MRR", `$${d.mrr.toLocaleString()}`, "kpi-mrr-detail"],
          ["Active subs", d.subscribers_active, "kpi-active-subs"],
          ["Total subs", d.subscribers_total, "kpi-total-subs"],
          ["Canceled 30d", d.canceled_in_window, "kpi-cancelled"],
        ].map(([label, val, tid]) => (
          <div
            key={label}
            data-testid={tid}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="text-xs uppercase text-slate-500">{label}</div>
            <div className="text-2xl font-bold text-slate-900">{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── AI Admin ───────────────────────────────────────────

export function PlatformAI() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState({ engines: [] });
  useEffect(() => {
    apiGet("/api/v3/platform/ai/usage-by-engine?window_days=30").then(setRows);
    apiGet("/api/v3/platform/ai/models").then(setModels);
  }, []);
  return (
    <div data-testid="platform-ai" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <Cpu className="w-4 h-4" /> AI Admin
        </div>
        <h1 className="text-2xl font-bold">Models & usage</h1>
      </header>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500 mb-2">Models</div>
        <div className="space-y-1">
          {models.engines.map((e) => (
            <div
              key={e.bp_tag}
              data-testid={`ai-model-${e.bp_tag}`}
              className="flex justify-between text-sm border-b border-slate-200 py-1"
            >
              <span className="text-slate-700">{e.bp_tag}</span>
              <span className="text-slate-600">{e.model}</span>
              <span className="text-slate-500">{e.prompt_version}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Engine</th>
              <th className="px-4 py-2">Collection</th>
              <th className="px-4 py-2">Requests</th>
              <th className="px-4 py-2">Cache hits</th>
              <th className="px-4 py-2">Hit rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((r) => (
              <tr key={r.bp_tag} data-testid={`ai-usage-${r.bp_tag}`}>
                <td className="px-4 py-2 text-slate-900">{r.bp_tag}</td>
                <td className="px-4 py-2 text-slate-600 font-mono text-xs">{r.collection}</td>
                <td className="px-4 py-2 text-slate-700">{r.requests}</td>
                <td className="px-4 py-2 text-slate-700">{r.cache_hits}</td>
                <td className="px-4 py-2 text-emerald-600">
                  {(r.cache_hit_rate * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ─── Health ─────────────────────────────────────────────

export function PlatformHealth() {
  const [h, setH] = useState(null);
  useEffect(() => {
    apiGet("/api/v3/platform/system-health").then(setH);
  }, []);
  if (!h) return <div className="p-8 text-slate-600">Loading…</div>;
  return (
    <div data-testid="platform-health" className="p-8 space-y-4 max-w-3xl">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <Server className="w-4 h-4" /> System health
        </div>
        <h1 className="text-2xl font-bold">Uptime & DB</h1>
      </header>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex justify-between items-center">
          <span className="text-slate-700">Database</span>
          <span
            data-testid="health-db"
            className={
              h.db_ok
                ? "rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs"
                : "rounded-full bg-red-500/20 text-red-300 px-2 py-0.5 text-xs"
            }
          >
            {h.db_ok ? "OK" : "DOWN"}
          </span>
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Latency {h.latency_ms} ms
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500 mb-2">Collections</div>
        <div className="grid grid-cols-2 gap-1 text-sm">
          {Object.entries(h.collections || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-200 py-1">
              <span className="text-slate-600 font-mono text-xs">{k}</span>
              <span className="text-slate-800">{v ?? "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Support ────────────────────────────────────────────

export function PlatformSupport() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    subject: "", description: "", priority: "medium", workspace_id: "",
  });
  const load = async () => {
    setRows(await apiGet("/api/v3/platform/support/tickets?limit=50"));
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.subject || !form.description) return toast.error("Fill required fields");
    try {
      await apiPost("/api/v3/platform/support/tickets", {
        ...form,
        workspace_id: form.workspace_id || null,
      });
      setForm({ subject: "", description: "", priority: "medium", workspace_id: "" });
      load();
    } catch (e) {
      toast.error(e?.message || "Create failed");
    }
  };
  const move = async (id, status) => {
    await apiPost(`/api/v3/platform/support/tickets/${id}/status`, { status });
    load();
  };
  return (
    <div data-testid="platform-support" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <LifeBuoy className="w-4 h-4" /> Support
        </div>
        <h1 className="text-2xl font-bold">Tickets</h1>
      </header>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500 mb-2">Open new</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            data-testid="support-subject"
            value={form.subject}
            onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
            placeholder="Subject"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm md:col-span-2"
          />
          <select
            data-testid="support-priority"
            value={form.priority}
            onChange={(e) => setForm((s) => ({ ...s, priority: e.target.value }))}
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          >
            {["low", "medium", "high", "urgent"].map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <button
            data-testid="support-create"
            onClick={create}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" /> Open
          </button>
          <textarea
            data-testid="support-desc"
            value={form.description}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
            placeholder="Description"
            rows={2}
            className="md:col-span-4 bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </div>
      </section>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Subject</th>
              <th className="px-4 py-2">Prio</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((t) => (
              <tr key={t.id} data-testid={`ticket-row-${t.id}`}>
                <td className="px-4 py-2 text-slate-900 truncate max-w-[380px]">
                  {t.subject}
                </td>
                <td className="px-4 py-2 text-slate-700">{t.priority}</td>
                <td className="px-4 py-2 text-slate-600">{t.status}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  {t.status !== "resolved" && (
                    <button
                      data-testid={`ticket-resolve-${t.id}`}
                      onClick={() => move(t.id, "resolved")}
                      className="rounded-md border border-emerald-700 text-emerald-700 hover:bg-emerald-900/40 px-2 py-1 text-xs"
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No tickets.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Announcements ──────────────────────────────────────

export function PlatformAnnouncements() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kind: "info", title: "", body_md: "", target: "all" });
  const load = async () => {
    setRows(await apiGet("/api/v3/platform/announcements?include_expired=true"));
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title || !form.body_md) return toast.error("Fill required fields");
    try {
      await apiPost("/api/v3/platform/announcements", form);
      setForm({ kind: "info", title: "", body_md: "", target: "all" });
      load();
    } catch (e) {
      toast.error(e?.message || "Create failed");
    }
  };
  const disable = async (id) => {
    await apiPost(`/api/v3/platform/announcements/${id}/deactivate`, {});
    load();
  };
  return (
    <div data-testid="platform-announcements" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <Megaphone className="w-4 h-4" /> Announcements
        </div>
        <h1 className="text-2xl font-bold">Broadcast messages</h1>
      </header>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <select
            data-testid="ann-kind"
            value={form.kind}
            onChange={(e) => setForm((s) => ({ ...s, kind: e.target.value }))}
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          >
            {["info", "warning", "maintenance"].map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
          <input
            data-testid="ann-title"
            value={form.title}
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
            placeholder="Title"
            className="md:col-span-2 bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <select
            data-testid="ann-target"
            value={form.target}
            onChange={(e) => setForm((s) => ({ ...s, target: e.target.value }))}
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          >
            <option>all</option>
            <option>platform_admins</option>
          </select>
          <button
            data-testid="ann-create"
            onClick={create}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" /> Publish
          </button>
          <textarea
            data-testid="ann-body"
            value={form.body_md}
            onChange={(e) => setForm((s) => ({ ...s, body_md: e.target.value }))}
            rows={3}
            placeholder="Body (markdown ok)"
            className="md:col-span-5 bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </div>
      </section>
      <div className="space-y-2">
        {rows.map((a) => (
          <div
            key={a.id}
            data-testid={`ann-row-${a.id}`}
            className={`rounded-xl border p-4 ${
              a.active ? "border-emerald-800 bg-white" : "border-slate-200 bg-white/30 opacity-70"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase text-slate-500">
                  {a.kind} · {a.target}
                </div>
                <div className="text-lg font-semibold text-slate-900">{a.title}</div>
              </div>
              {a.active && (
                <button
                  data-testid={`ann-disable-${a.id}`}
                  onClick={() => disable(a.id)}
                  className="rounded-md border border-red-700 text-red-300 hover:bg-red-900/40 px-3 py-1 text-xs"
                >
                  Deactivate
                </button>
              )}
            </div>
            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
              {a.body_md}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
            No announcements.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Roles ──────────────────────────────────────────────

export function PlatformRoles() {
  const [data, setData] = useState({ assignments: [], roles: [] });
  const [form, setForm] = useState({ user_id: "", role: "platform_admin", reason: "" });
  const load = async () => setData(await apiGet("/api/v3/platform/roles"));
  useEffect(() => { load(); }, []);
  const grant = async () => {
    if (!form.user_id) return toast.error("user_id required");
    try {
      await apiPost("/api/v3/platform/roles/grant", form);
      toast.success("Granted");
      setForm({ user_id: "", role: "platform_admin", reason: "" });
      load();
    } catch (e) {
      toast.error(e?.message || "Grant failed");
    }
  };
  const revoke = async (user_id, role) => {
    if (!confirm(`Revoke ${role} from ${user_id}?`)) return;
    await apiPost("/api/v3/platform/roles/revoke", {
      user_id, role, reason: "revoked via console",
    });
    load();
  };
  return (
    <div data-testid="platform-roles" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <UserCog className="w-4 h-4" /> Roles
        </div>
        <h1 className="text-2xl font-bold">Platform role assignments</h1>
      </header>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500 mb-2">Grant role</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            data-testid="role-user-id"
            value={form.user_id}
            onChange={(e) => setForm((s) => ({ ...s, user_id: e.target.value }))}
            placeholder="user_id"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <select
            data-testid="role-choice"
            value={form.role}
            onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          >
            {(data.roles || []).map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <input
            data-testid="role-reason"
            value={form.reason}
            onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))}
            placeholder="Reason"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <button
            data-testid="role-grant"
            onClick={grant}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" /> Grant
          </button>
        </div>
      </section>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">User</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Granted</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {(data.assignments || []).map((a) => (
              <tr key={`${a.user_id}-${a.role}`} data-testid={`role-row-${a.user_id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-700 truncate max-w-[220px]">
                  {a.user_id}
                </td>
                <td className="px-4 py-2 text-slate-900">{a.role}</td>
                <td className="px-4 py-2 text-slate-500">
                  {a.granted_at?.slice(0, 19)}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    data-testid={`role-revoke-${a.user_id}`}
                    onClick={() => revoke(a.user_id, a.role)}
                    className="rounded-md border border-red-700 text-red-300 hover:bg-red-900/40 px-2 py-1 text-xs"
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
